import { useMemo } from "react";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useColumnSortFunc } from "../blocks/ColumnSort/useColumnSortFunc";
import { useLeadsCriteriaPredicate } from "../blocks/MyQuotes/filters/Criteria/useLeadsCriteriaPredicate";
import { useLeadsDatePredicate } from "../blocks/MyQuotes/filters/Date/useLeadsDatePredicate";
import { useOrdersSizeLimit } from "../blocks/MyQuotes/filters/Size/useOrdersSizeLimit";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { useUrl } from "../stores/hooks/useUrl";

const dateFields = new Set(["createdDate", "requestedDate"]);
const numericFields = new Set([]);

export const useGetDealerLeads = (): ModifiedSWRResponse<
  FetcherResult<Payload>,
  MappedPayload
> => {
  const { dealerId } = useDealerAccountInfo();
  const size = useOrdersSizeLimit();
  const datePredicate = useLeadsDatePredicate();
  const criteriaPredicate = useLeadsCriteriaPredicate();
  const { search } = useUrl();
  const { data, ...rest } = useSwrGet<LeadItem[]>(
    "/retailSiteWebService/shopping/lead/getDealerLeads.php",
    {
      ageDays: "30",
      dealerId,
    },
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
    },
  );
  const sortFunc = useColumnSortFunc("leadReport", {
    dateFields,
    numericFields,
  });
  const result = useMemo(
    () =>
      data
        ?.filter(datePredicate)
        .filter(criteriaPredicate)
        .map(
          ({
            createdTimestamp,
            customer,
            customerStatus,
            date,
            generatedByActionType,
            leadId,
            message,
            orderId,
            ownerType,
            time,
          }) => {
            return {
              appointmentRequestTime: time,
              createdDate: createdTimestamp.date,
              customerEmail: customer.email,
              customerName: [customer.firstName, customer.lastName]
                .filter(Boolean)
                .join(" "),
              customerNotes: message,
              customerPhone: customer.phone,
              generatedByActionType,
              leadId,
              orderId,
              ownerType,
              requestedDate: date,
              status: customerStatus.status,
            };
          },
        )
        .sort(sortFunc)
        .slice(0, size),
    [data, search, sortFunc, datePredicate, criteriaPredicate],
  );
  return {
    data: result,
    ...rest,
  };
};

type Payload = LeadItem[];

type MappedPayload = MappedLeadItem[];

export type LeadItem = {
  authorType: string;
  createdTimestamp: {
    date: string;
    timezone: string;
    timezone_type: number;
  };
  createdTokenId: string;
  customer: {
    contactMethodPreference: string;
    email: string;
    firstName: string;
    language: string;
    lastName: string;
    phone: string;
  };
  customerStatus: {
    status: string;
    timestamp: string;
    userId: string;
  };
  date: null | string;
  dealerStatus: {
    status: string;
    timestamp: string;
    userId: string;
  };
  generatedByActionType: string;
  internalNote: string;
  isNationalLead: string;
  leadId: string;
  leadType: string;
  message: string;
  orderId: string;
  ownerId: string;
  ownerType: string;
  parentLeadId: number;
  time: null | string;
};

export type MappedLeadItem = {
  appointmentRequestTime: null | string;
  createdDate: string;
  customerEmail: string;
  customerName: string;
  customerNotes: string;
  customerPhone: string;
  generatedByActionType: string;
  leadId: string;
  orderId: string;
  ownerType: string;
  requestedDate: null | string;
  status: string;
};
